import { useEffect } from "react";

const findFocusTarget = (host) =>
   host.querySelector('input:not([type="hidden"]):not([disabled])') ||
   host.querySelector("textarea") ||
   host.querySelector('[role="combobox"]:not([aria-disabled="true"])') ||
   host.querySelector("select:not([disabled])") ||
   host.querySelector('[tabindex]:not([tabindex="-1"])') ||
   host;

/**
 * While `editing` is set, blocks all pointer/keyboard events outside the active cell
 * and disables pointer interaction on sibling tables.
 * ESC is allowed through so callers can cancel editing.
 */
export const useFocusLock = ({
   editing,
   tableRootRef,
   editingHostRef,
   isInAllowedPortal,
   didInitialSelectRef,
}) => {
   useEffect(() => {
      if (!editing) return;
      const root = tableRootRef.current;
      if (!root) return;

      const hostSelector = `[data-grid-cell="true"][data-row="${editing.row}"][data-col="${editing.col}"]`;
      const getHostCell = () => root.querySelector(hostSelector) || editingHostRef.current || null;

      const reassert = () => {
         const host = getHostCell();
         if (!host) return;
         const el = findFocusTarget(host);
         const run = () => {
            try {
               el?.focus?.({ preventScroll: true });
            } catch {
               el?.focus?.();
            }
            if (!didInitialSelectRef.current && typeof el?.select === "function") {
               didInitialSelectRef.current = true;
               el.select();
            }
         };
         run();
         requestAnimationFrame(run);
         setTimeout(run, 0);
         setTimeout(run, 16);
      };

      // Disable pointer events on sibling tables while editing
      const disabledSnapshot = new Map();
      Array.from(document.querySelectorAll('[data-table-widget="true"]'))
         .filter((tbl) => tbl !== root)
         .forEach((tbl) => {
            disabledSnapshot.set(tbl, {
               pe: tbl.style.pointerEvents,
               aria: tbl.getAttribute("aria-disabled"),
            });
            tbl.style.pointerEvents = "none";
            tbl.setAttribute("aria-disabled", "true");
         });

      const allow = (t) => {
         if (!t) return false;
         if (isInAllowedPortal(t)) return true;
         const host = getHostCell();
         return !!(host && host.contains(t));
      };

      const stop = (e) => {
         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation?.();
         reassert();
      };

      const blockIfNotAllowed = (e) => {
         if (!allow(e.target)) stop(e);
      };

      const onFocusOut = (e) => {
         const host = getHostCell();
         if (!host) return;
         if (!(e.target instanceof Element) || !host.contains(e.target)) return;
         if (allow(e.relatedTarget)) return;
         stop(e);
      };

      const onKeyDown = (e) => {
         if (e.key === "Escape") return; // ESC może zamknąć edycję
         if (!allow(e.target)) stop(e);
      };

      const listeners = [
         ["pointerdown", blockIfNotAllowed],
         ["pointerup", blockIfNotAllowed],
         ["mousedown", blockIfNotAllowed],
         ["mouseup", blockIfNotAllowed],
         ["click", blockIfNotAllowed],
         ["focusin", blockIfNotAllowed],
         ["focusout", onFocusOut],
         ["keydown", onKeyDown],
      ];
      listeners.forEach(([type, handler]) => document.addEventListener(type, handler, true));
      reassert();

      return () => {
         listeners.forEach(([type, handler]) => document.removeEventListener(type, handler, true));
         disabledSnapshot.forEach((snap, tbl) => {
            tbl.style.pointerEvents = snap.pe;
            if (snap.aria == null) tbl.removeAttribute("aria-disabled");
            else tbl.setAttribute("aria-disabled", snap.aria);
         });
      };
   }, [editing, isInAllowedPortal, tableRootRef]); // editingHostRef i didInitialSelectRef są refami — stabilne
};
